import { useState, useMemo, useCallback, useEffect, useRef, Fragment } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { SubGrid, SubGridContent } from '@/components/ui/sub-grid'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table'
import {
  AnimatedTableWrapper,
  AnimatedTableBody,
  AnimatedTableRow,
} from '@/components/tailwindadmin/components/animated-component/AnimatedTable'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/tailwindadmin/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/tailwindadmin/components/ui/dropdown-menu'
import { Check, X, Minus, Download, Search, Settings2, Users, Building2, AlertCircle, ChevronRight, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { SortableHeader } from '@/views/equip/components/SortableHeader'
import { cn } from '@/lib/utils'
import { equipApi } from '@/services/equipApi'
import { getActiveMeasures as getActiveMeasuresUtil, getMeasureDisplayName } from '@/lib/equip-utils'
import { useMeasureThresholdMap } from '@/hooks/equip/useMeasureThresholdMap'
import type { ProviderPerformanceDetail, PatientDrilldownItem } from '@/lib/equip-types'
import { equipTable } from '@/views/equip/equip-table-styles'

interface FullPatientHeatmapProps {
  performance: ProviderPerformanceDetail
  providerNpi: string
  year: number
  quarter: number
  isLoading: boolean
  onPatientClick: (patient: PatientDrilldownItem) => void
  providerName?: string
  providerEmail?: string
  acoFilter?: 'all' | 'aco'
  viewMode?: 'patient' | 'facility'
  onToggleAcoFilter?: () => void
  onToggleViewMode?: () => void
  isLeader?: boolean
}

interface PatientHeatmapRow {
  patientId: number
  patientName: string
  facilityId: string
  facilityName: string
  isAco: boolean
  measures: Record<string, MeasureStatus>
}

interface MeasureStatus {
  isEligible: boolean
  isMet: boolean
}

export default function FullPatientHeatmap({
  performance,
  providerNpi,
  year,
  quarter,
  isLoading,
  onPatientClick,
  providerName: _providerName,
  providerEmail: _providerEmail,
  acoFilter: propAcoFilter,
  viewMode: propViewMode,
  onToggleAcoFilter,
  onToggleViewMode,
  isLeader = false,
}: FullPatientHeatmapProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [patients, setPatients] = useState<PatientHeatmapRow[]>([])
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [loadingProgress, setLoadingProgress] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  // Use parent-controlled props with defaults
  const activeAcoFilter = propAcoFilter || 'all'
  const activeViewMode = propViewMode || 'patient'

  // Only outcome measures belong on the patient heatmap (not PIMs/Coding/Quality Audit/ED Sendout)
  const HEATMAP_MEASURES = ['AWV', 'A1C', 'BCS', 'CRCS', 'PHQ9', 'HTN', 'ACP', 'ACO_Visits']

  const thresholdMap = useMeasureThresholdMap(year, quarter)

  const activeMeasureData = useMemo(
    () =>
      getActiveMeasuresUtil(performance, thresholdMap, isLeader).filter((m) =>
        HEATMAP_MEASURES.includes(m.measureName)
      ),
    [performance, thresholdMap, isLeader]
  )

  const activeMeasures = useMemo(
    () => activeMeasureData.map((m) => m.measureName),
    [activeMeasureData]
  )

  // Communicated (display) pass threshold per measure, 0-100 scale, sourced
  // from och.equip_config_measure_schedule for this provider's role via
  // getActiveMeasures. Drives the green/red boundary so A1C/BCS/CRCS (45% in
  // Q2) aren't painted red against a hardcoded 90. Falls back to 90 if missing.
  const measureDisplayThreshold = useMemo(() => {
    const map = new Map<string, number>()
    for (const m of activeMeasureData) map.set(m.measureName, m.thresholdDisplay)
    return map
  }, [activeMeasureData])

  // Mapping from measure name to per-measure eligible/met fields on the ALL response
  const MEASURE_FIELD_MAP: Record<string, { eligible: keyof PatientDrilldownItem; met: keyof PatientDrilldownItem }> = {
    'A1C': { eligible: 'a1cEligible', met: 'a1cMet' },
    'BCS': { eligible: 'bcsEligible', met: 'bcsMet' },
    'CRCS': { eligible: 'crcsEligible', met: 'crcsMeasureMet' },
    'PHQ9': { eligible: 'phq9Eligible', met: 'phq9Met' },
    'HTN': { eligible: 'htnEligible', met: 'htnMet' },
    'ACP': { eligible: 'acpEligible', met: 'acpMet' },
    'ACO_Visits': { eligible: 'acoVisitsEligible', met: 'acoVisitsMet' },
  }

  // Fetch ALL patients in a single call
  useEffect(() => {
    if (!providerNpi) return

    const fetchAllPatients = async () => {
      setLoadingProgress('Loading patient data...')
      setError(null)

      try {
        const drilldown = await equipApi.patients.getDrilldown(
          providerNpi,
          'ALL',
          { year, quarter }
        )

        const rows: PatientHeatmapRow[] = drilldown.map((item) => {
          const measures: Record<string, MeasureStatus> = {}

          for (const measure of activeMeasures) {
            const mapping = MEASURE_FIELD_MAP[measure]
            if (mapping) {
              const eligible = !!(item[mapping.eligible] as boolean | undefined)
              const met = !!(item[mapping.met] as boolean | undefined)
              measures[measure] = { isEligible: eligible, isMet: met }
            } else if (measure === 'AWV') {
              // AWV: awvComplete != null → eligible.
              // met uses the server-derived awvMet so the heatmap matches the
              // patient modal exactly (same _derive_awv_met derivation) (#347).
              const eligible = item.awvComplete != null
              const met = item.awvMet === true
              measures[measure] = { isEligible: eligible, isMet: met }
            }
          }

          return {
            patientId: item.patientId,
            patientName: item.patientName || `Patient ${item.patientId}`,
            facilityId: item.facilityId,
            facilityName: item.facilityName || item.facilityId,
            isAco: item.isAco ?? false,
            measures,
          }
        })

        setPatients(rows)
        setLoadingProgress('')
      } catch (err) {
        console.error('Error loading patient heatmap data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load patient data')
        setLoadingProgress('')
      }
    }

    fetchAllPatients()
  }, [providerNpi, year, quarter, activeMeasures])

  // Create column helper
  const columnHelper = createColumnHelper<PatientHeatmapRow>()

  // Helper for measure cell rendering
  const renderMeasureCell = (status: MeasureStatus | undefined) => {
    if (!status || !status.isEligible) {
      return (
        <div className="flex items-center justify-center">
          <Badge variant="muted" className="px-1">
            <Minus className="h-3 w-3" />
          </Badge>
        </div>
      )
    }

    if (status.isMet) {
      return (
        <div className="flex items-center justify-center bg-emerald-100 dark:bg-emerald-500/35 p-1 rounded">
          <Badge variant="lightSuccess" className="px-1 text-emerald-700 dark:text-emerald-300">
            <Check className="h-3 w-3" />
          </Badge>
        </div>
      )
    }

    return (
      <div className="flex items-center justify-center bg-lighterror p-1 rounded">
        <Badge variant="lightError" className="px-1">
          <X className="h-3 w-3" />
        </Badge>
      </div>
    )
  }

  // Dynamic columns based on active measures
  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('patientName', {
        header: ({ column }) => <SortableHeader label="Patient Name" column={column} />,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium">{row.original.patientName}</span>
            {row.original.isAco && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-500/25 dark:text-blue-300 leading-none">
                ACO
              </span>
            )}
          </div>
        ),
      }),
      columnHelper.accessor('facilityName', {
        header: ({ column }) => <SortableHeader label="Facility" column={column} />,
        cell: ({ row }) => (
          <p className="text-sm">{row.original.facilityName}</p>
        ),
      }),
    ]

    // Add measure columns
    const measureColumns = activeMeasures.map((measure) =>
      columnHelper.display({
        id: measure,
        header: ({ column }) => <SortableHeader label={getMeasureDisplayName(measure)} column={column} />,
        cell: ({ row }) => renderMeasureCell(row.original.measures[measure]),
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.measures[measure]
          const b = rowB.original.measures[measure]

          // Sort: met > unmet > not eligible
          const getScore = (status: MeasureStatus | undefined) => {
            if (!status || !status.isEligible) return 0
            if (status.isMet) return 2
            return 1
          }

          return getScore(b) - getScore(a)
        },
      })
    )

    return [...baseColumns, ...measureColumns]
  }, [columnHelper, activeMeasures])

  // Filter patients by ACO status (if applicable)
  const filteredPatients = useMemo(() => {
    if (activeAcoFilter === 'all') return patients

    // Filter by ACO status using the aco_patient flag from the backend
    return patients.filter((p) => p.isAco)
  }, [patients, activeAcoFilter])

  // Derive facility groups from already-fetched patient data (client-side, no API call)
  // Fixes HEAT-01 by populating the Facilities view with actual rows instead of placeholder
  const facilityGroups = useMemo(() => {
    const map = new Map<string, { facilityId: string; facilityName: string; patients: PatientHeatmapRow[] }>()
    for (const p of filteredPatients) {
      const key = p.facilityId || 'Unknown'
      if (!map.has(key)) {
        map.set(key, { facilityId: key, facilityName: p.facilityName || key, patients: [] })
      }
      map.get(key)!.patients.push(p)
    }
    return Array.from(map.values()).sort((a, b) => b.patients.length - a.patients.length)
  }, [filteredPatients])

  // Track which facility row is expanded in the facility view
  const [expandedFacilityId, setExpandedFacilityId] = useState<string | null>(null)
  const [facilitySortKey, setFacilitySortKey] = useState<string>('facilityId')
  const [facilitySortDir, setFacilitySortDir] = useState<'asc' | 'desc'>('asc')

  const sortedFacilityGroups = useMemo(() => {
    return [...facilityGroups].sort((a, b) => {
      const dir = facilitySortDir === 'asc' ? 1 : -1
      if (facilitySortKey === 'facilityId') {
        return a.facilityName.localeCompare(b.facilityName) * dir
      }
      if (facilitySortKey === 'patients') {
        return (a.patients.length - b.patients.length) * dir
      }
      // Measure sort — compare pass rates
      const aEligible = a.patients.filter(p => p.measures[facilitySortKey]?.isEligible).length
      const aMet = a.patients.filter(p => p.measures[facilitySortKey]?.isMet).length
      const aRate = aEligible > 0 ? aMet / aEligible : 0
      const bEligible = b.patients.filter(p => p.measures[facilitySortKey]?.isEligible).length
      const bMet = b.patients.filter(p => p.measures[facilitySortKey]?.isMet).length
      const bRate = bEligible > 0 ? bMet / bEligible : 0
      return (aRate - bRate) * dir
    })
  }, [facilityGroups, facilitySortKey, facilitySortDir])

  const handleFacilitySort = (key: string) => {
    if (facilitySortKey === key) {
      setFacilitySortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setFacilitySortKey(key)
      setFacilitySortDir('asc')
    }
  }

  const table = useReactTable({
    data: filteredPatients,
    columns,
    state: {
      sorting,
      globalFilter,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  })

  // CSV export handler
  const handleExportCSV = useCallback(() => {
    const headers = ['Patient ID', 'Patient Name', 'Facility', ...activeMeasures]
    const rows = filteredPatients.map((p) => [
      p.patientId,
      p.patientName,
      p.facilityId,
      ...activeMeasures.map((m) => {
        const status = p.measures[m]
        if (!status || !status.isEligible) return 'N/A'
        return status.isMet ? 'Met' : 'Unmet'
      }),
    ])
    const csvContent = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')),
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      `patient-heatmap-${providerNpi}-${year}-Q${quarter}.csv`
    )
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [filteredPatients, activeMeasures, providerNpi, year, quarter])

  if (isLoading || loadingProgress) {
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">
          {loadingProgress || 'Loading patient heatmap...'}
        </p>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 p-4 text-sm text-destructive bg-destructive/10 rounded-md">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Patient Heatmap</h3>
          <div className="flex items-center gap-2">
            {/* ACO filter toggle */}
            <div className="flex items-center gap-1 border rounded-md p-1">
              <Button
                variant={activeAcoFilter === 'all' ? 'default' : 'ghost'}
                size="sm"
                onClick={onToggleAcoFilter}
              >
                All Patients
              </Button>
              <Button
                variant={activeAcoFilter === 'aco' ? 'default' : 'ghost'}
                size="sm"
                onClick={onToggleAcoFilter}
              >
                ACO Only
              </Button>
            </div>

            {/* Patient/Facility view toggle */}
            <div className="flex items-center gap-1 border rounded-md p-1">
              <Button
                variant={activeViewMode === 'patient' ? 'default' : 'ghost'}
                size="sm"
                onClick={onToggleViewMode}
              >
                <Users className="h-4 w-4 mr-1" />
                Patients
              </Button>
              <Button
                variant={activeViewMode === 'facility' ? 'default' : 'ghost'}
                size="sm"
                onClick={onToggleViewMode}
              >
                <Building2 className="h-4 w-4 mr-1" />
                Facilities
              </Button>
            </div>

            {/* Search toggle */}
            {!showSearch ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSearch(true)}
              >
                <Search className="h-4 w-4" />
              </Button>
            ) : (
              <Input
                placeholder="Search patients..."
                className="w-56"
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                onBlur={() => {
                  if (!globalFilter) setShowSearch(false)
                }}
              />
            )}

            {/* Column visibility */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Settings2 className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {activeMeasures.map((measure) => (
                  <DropdownMenuItem
                    key={measure}
                    className="flex items-center cursor-pointer"
                    onSelect={(event) => {
                      event.preventDefault()
                      setColumnVisibility((prev) => ({
                        ...prev,
                        [measure]: !(prev[measure] ?? true),
                      }))
                    }}
                  >
                    <Checkbox
                      checked={columnVisibility[measure] !== false}
                      onCheckedChange={() => {
                        setColumnVisibility((prev) => ({
                          ...prev,
                          [measure]: !(prev[measure] ?? true),
                        }))
                      }}
                      className="mr-2"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>{measure}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* CSV export */}
            <Button variant="ghost" size="icon" onClick={handleExportCSV}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Facility View Mode */}
        {activeViewMode === 'facility' ? (
          <div className={equipTable.wrapper}>
            <div className="overflow-auto">
              <table className={equipTable.table}>
                <thead className={equipTable.thead}>
                  <tr className={equipTable.theadTr}>
                    <th className={equipTable.th} style={{ width: 32 }} />
                    <th
                      className={cn(equipTable.th, equipTable.stickyTh, 'cursor-pointer select-none')}
                      onClick={() => handleFacilitySort('facilityId')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Facility
                        {facilitySortKey === 'facilityId' ? (
                          facilitySortDir === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ChevronsUpDown className="h-4 w-4 opacity-50" />
                        )}
                      </span>
                    </th>
                    <th
                      className={cn(equipTable.th, 'cursor-pointer select-none')}
                      onClick={() => handleFacilitySort('patients')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Patients
                        {facilitySortKey === 'patients' ? (
                          facilitySortDir === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ChevronsUpDown className="h-4 w-4 opacity-50" />
                        )}
                      </span>
                    </th>
                    {activeMeasures.map((m) => (
                      <th
                        key={m}
                        className={cn(equipTable.th, 'cursor-pointer select-none')}
                        onClick={() => handleFacilitySort(m)}
                      >
                        <span className="inline-flex items-center gap-1">
                          {getMeasureDisplayName(m)}
                          {facilitySortKey === m ? (
                            facilitySortDir === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ChevronsUpDown className="h-4 w-4 opacity-50" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedFacilityGroups.length === 0 ? (
                    <tr>
                      <td colSpan={3 + activeMeasures.length} className="text-center py-8 text-muted-foreground">
                        No facility data available
                      </td>
                    </tr>
                  ) : (
                    sortedFacilityGroups.map((group, groupIndex) => {
                      const isFacilityExpanded = expandedFacilityId === group.facilityId
                      const isLast = groupIndex === sortedFacilityGroups.length - 1

                      // Aggregate measure pass rates across this facility's patients
                      const measureSummary = activeMeasures.reduce(
                        (acc, measure) => {
                          const eligible = group.patients.filter((p) => p.measures[measure]?.isEligible).length
                          const met = group.patients.filter((p) => p.measures[measure]?.isMet).length
                          acc[measure] = { eligible, met }
                          return acc
                        },
                        {} as Record<string, { eligible: number; met: number }>
                      )

                      return (
                        <Fragment key={group.facilityId}>
                          {/* Facility header row */}
                          <tr
                            className={cn(equipTable.trClickable, isFacilityExpanded && 'bg-muted/30')}
                            onClick={() =>
                              setExpandedFacilityId(isFacilityExpanded ? null : group.facilityId)
                            }
                          >
                            <td className={equipTable.td}>
                              <ChevronRight
                                className={cn(
                                  'h-4 w-4 text-muted-foreground transition-transform duration-200',
                                  isFacilityExpanded && 'rotate-90'
                                )}
                              />
                            </td>
                            <td className={cn(equipTable.td, isFacilityExpanded ? equipTable.stickyTdAlt : equipTable.stickyTd)}>
                              <span className="font-medium text-sm">{group.facilityName}</span>
                            </td>
                            <td className={equipTable.td}>
                              <Badge variant="outline" className="text-xs font-semibold">
                                {group.patients.length}
                              </Badge>
                            </td>
                            {activeMeasures.map((m) => {
                              const { eligible, met } = measureSummary[m]
                              if (eligible === 0) {
                                return (
                                  <td key={m} className={equipTable.td}>
                                    <span className="text-xs text-muted-foreground">—</span>
                                  </td>
                                )
                              }
                              const pct = Math.round((met / eligible) * 100)
                              const greenAt = measureDisplayThreshold.get(m) ?? 90
                              return (
                                <td key={m} className={equipTable.td}>
                                  <span className={cn(
                                    'text-xs font-medium',
                                    pct >= greenAt ? 'text-green-600' : pct >= 70 ? 'text-yellow-600' : 'text-red-600'
                                  )}>
                                    {pct}%
                                  </span>
                                  <span className="text-[10px] text-muted-foreground ml-1">({met}/{eligible})</span>
                                </td>
                              )
                            })}
                          </tr>

                          {/* Expanded SubGrid with individual patients */}
                          <AnimatePresence initial={false}>
                            {isFacilityExpanded && (
                              <tr>
                                <td colSpan={3 + activeMeasures.length} className="p-0">
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{
                                      height: { duration: 0.4, ease: [0.32, 0.72, 0, 1] },
                                      opacity: { duration: 0.25, delay: 0.08 },
                                    }}
                                    className="overflow-hidden"
                                  >
                                    <SubGrid isExpanded={true} depth={1} variant="compact" isLast={isLast}>
                                      <SubGridContent>
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className={equipTable.theadTr}>
                                              <th className={equipTable.th}>Patient</th>
                                              {activeMeasures.map((m) => (
                                                <th key={m} className={cn(equipTable.th, 'text-center')}>
                                                  {getMeasureDisplayName(m)}
                                                </th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {group.patients.map((patient) => (
                                              <tr
                                                key={patient.patientId}
                                                className={equipTable.trClickable}
                                                onClick={(e) => {
                                                  e.stopPropagation()
                                                  onPatientClick({
                                                    patientId: patient.patientId,
                                                    providerName: patient.patientName,
                                                    facilityId: patient.facilityId,
                                                    isEligible: true,
                                                    isMet: false,
                                                  })
                                                }}
                                              >
                                                <td className={equipTable.td}>
                                                  <span className="font-medium">{patient.patientName}</span>
                                                </td>
                                                {activeMeasures.map((m) => (
                                                  <td key={m} className={cn(equipTable.td, 'text-center')}>
                                                    {renderMeasureCell(patient.measures[m])}
                                                  </td>
                                                ))}
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </SubGridContent>
                                    </SubGrid>
                                  </motion.div>
                                </td>
                              </tr>
                            )}
                          </AnimatePresence>
                        </Fragment>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Patient View Mode (Existing Table) */
          <>
            {/* Table */}
            <div className={equipTable.wrapper}>
              <div
                ref={scrollContainerRef}
                className="overflow-auto"
                style={{
                  maxHeight: 44 + table.getState().pagination.pageSize * 45,
                }}
              >
              <AnimatedTableWrapper className="overflow-x-auto">
                <table className={equipTable.table}>
                  <thead className={equipTable.thead}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className={equipTable.theadTr}>
                    {headerGroup.headers.map((header, headerIdx) => (
                      <th
                        key={header.id}
                        className={cn(
                          equipTable.th,
                          header.column.getCanSort() && 'cursor-pointer select-none',
                          headerIdx === 0 && equipTable.stickyTh
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <AnimatedTableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-center py-4 text-muted-foreground"
                    >
                      No patients found
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, index) => (
                    <AnimatedTableRow
                      key={row.id}
                      index={index}
                      className={equipTable.trClickable}
                      onClick={() => {
                        // Create a PatientDrilldownItem for onClick
                        const patient: PatientDrilldownItem = {
                          patientId: row.original.patientId,
                          providerName: row.original.patientName,
                          facilityId: row.original.facilityId,
                          isEligible: true,
                          isMet: false,
                        }
                        onPatientClick(patient)
                      }}
                    >
                      {row.getVisibleCells().map((cell, cellIdx) => (
                        <td
                          key={cell.id}
                          className={cn(equipTable.td, cellIdx === 0 && equipTable.stickyTd)}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </AnimatedTableRow>
                  ))
                )}
              </AnimatedTableBody>
            </table>
          </AnimatedTableWrapper>
              </div>
        </div>

        {/* Pagination Controls */}
        {table.getPageCount() > 0 && (
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">Show</p>
              <Select
                value={String(table.getState().pagination.pageSize)}
                onValueChange={(value) => table.setPageSize(Number(value))}
              >
                <SelectTrigger className="w-fit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[25, 50, 100, 200].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">per page</p>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-sm text-muted-foreground">
                {table.getRowModel().rows.length > 0
                  ? `${
                      table.getState().pagination.pageIndex *
                        table.getState().pagination.pageSize +
                      1
                    }-${Math.min(
                      (table.getState().pagination.pageIndex + 1) *
                        table.getState().pagination.pageSize,
                      table.getFilteredRowModel().rows.length
                    )} of ${table.getFilteredRowModel().rows.length}`
                  : '0 of 0'}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="h-8 w-8"
                >
                  ←
                </Button>
                <span className="text-sm font-medium">
                  {table.getState().pagination.pageIndex + 1}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="h-8 w-8"
                >
                  →
                </Button>
              </div>
            </div>
          </div>
        )}

          </>
        )}
      </div>
    </Card>
  )
}
